'use client';

/**
 * Cloudflare Turnstile, rendered only when the API asks for it (a
 * `TURNSTILE_REQUIRED` refusal carries the site key). The script is loaded on
 * first use — a guest who is never challenged never downloads it.
 *
 * Tokens are single-use: the parent resubmits with the token it gets from
 * `onToken`, and remounts this widget (new `key`) to get a fresh one if the
 * API challenges again.
 */
import { useEffect, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
    render: (el: HTMLElement, opts: Record<string, unknown>) => string;
    remove: (id: string) => void;
}

declare global {
    interface Window {
        turnstile?: TurnstileApi;
    }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!scriptPromise) {
        scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = SCRIPT_SRC;
            script.async = true;
            script.defer = true;
            script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile missing')));
            script.onerror = () => {
                scriptPromise = null;
                reject(new Error('turnstile failed to load'));
            };
            document.head.appendChild(script);
        });
    }
    return scriptPromise;
}

export interface TurnstileWidgetProps {
    siteKey: string;
    onToken: (token: string) => void;
    onError?: () => void;
}

export function TurnstileWidget({ siteKey, onToken, onError }: TurnstileWidgetProps) {
    const ref = useRef<HTMLDivElement>(null);
    const onTokenRef = useRef(onToken);
    const onErrorRef = useRef(onError);
    onTokenRef.current = onToken;
    onErrorRef.current = onError;

    useEffect(() => {
        let widgetId: string | null = null;
        let cancelled = false;
        loadTurnstile()
            .then((api) => {
                if (cancelled || !ref.current) return;
                widgetId = api.render(ref.current, {
                    sitekey: siteKey,
                    callback: (token: string) => onTokenRef.current(token),
                    'error-callback': () => onErrorRef.current?.(),
                });
            })
            .catch(() => onErrorRef.current?.());
        return () => {
            cancelled = true;
            if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
        };
    }, [siteKey]);

    return <div ref={ref} data-testid="turnstile-widget" />;
}
